/**
 * Platform Shared Types
 * Core type definitions for the Marketing & Sales Platform
 */

// ============================================================================
// TENANT TYPES
// ============================================================================

export type TenantType = 'platform' | 'enterprise' | 'agency' | 'reseller';
export type TenantStatus = 'pending' | 'active' | 'suspended' | 'cancelled';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | 'unpaid';

export interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  legalName?: string;
  tenantType: TenantType;
  status: TenantStatus;
  planId?: string;
  planSlug?: string;
  features: string[];
  modules: string[];
  branding: TenantBranding;
  limits: TenantLimits;
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
}

export interface TenantBranding {
  companyName: string;
  logoUrl?: string;
  logoDarkUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  themeConfig?: ThemeConfig;
  customCss?: string;
  emailLogoUrl?: string;
  emailFooterText?: string;
}

export interface TenantLimits {
  maxUsers: number;
  maxContacts: number;
  maxMonthlyEmails: number;
  maxStorageGb: number;
  maxCustomDomains: number;
  maxApiCallsPerMonth: number;
}

export interface TenantSettings {
  timezone?: string;
  dateFormat?: string;
  currency?: string;
  language?: string;
  [key: string]: unknown;
}

// ============================================================================
// THEME TYPES
// ============================================================================

export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

export interface ThemeColors {
  primary: ColorScale;
  secondary: ColorScale;
  accent: ColorScale;
  neutral: ColorScale;
  success: ColorScale;
  warning: ColorScale;
  error: ColorScale;
  info: ColorScale;
}

export interface ThemeTypography {
  fontFamily: string;
  headingFont: string;
  monoFont: string;
  baseFontSize: string;
  lineHeight: string;
}

export interface ThemeSpacing {
  unit: number;
  borderRadius: string;
  containerWidth: string;
}

export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface ThemeConfig {
  colors?: Partial<ThemeColors>;
  typography?: Partial<ThemeTypography>;
  spacing?: Partial<ThemeSpacing>;
  shadows?: Partial<ThemeShadows>;
  components?: Record<string, unknown>;
}

// ============================================================================
// DOMAIN TYPES
// ============================================================================

export type DomainType = 'primary' | 'app' | 'portal' | 'api' | 'email';
export type SSLStatus = 'pending' | 'active' | 'failed' | 'expired';

export interface TenantDomain {
  id: string;
  tenantId: string;
  domain: string;
  domainType: DomainType;
  sslStatus: SSLStatus;
  sslCertificateId?: string;
  sslExpiresAt?: string;
  dnsVerified: boolean;
  dnsVerificationToken?: string;
  dnsVerifiedAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// PLAN TYPES
// ============================================================================

export type PlanTier = 'starter' | 'professional' | 'enterprise' | 'custom';

export interface PlatformPlan {
  id: string;
  name: string;
  slug: string;
  description?: string;
  tier: PlanTier;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  maxUsers: number;
  maxContacts: number;
  maxMonthlyEmails: number;
  maxStorageGb: number;
  maxCustomDomains: number;
  maxApiCallsPerMonth: number;
  featuresIncluded: string[];
  modulesIncluded: string[];
  isPopular: boolean;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

// ============================================================================
// FEATURE FLAG TYPES
// ============================================================================

export enum FeatureFlag {
  // Modules
  MODULE_WEB_PRESENCE = 'module.web_presence',
  MODULE_MARKETING = 'module.marketing',
  MODULE_SALES = 'module.sales',
  MODULE_REVOPS = 'module.revops',
  MODULE_SUPPORT = 'module.support',
  MODULE_INTENT_INTELLIGENCE = 'module.intent_intelligence',

  // Marketing Features
  MARKETING_EMAIL_CAMPAIGNS = 'marketing.email_campaigns',
  MARKETING_NURTURE_PROGRAMS = 'marketing.nurture_programs',
  MARKETING_LEAD_SCORING = 'marketing.lead_scoring',
  MARKETING_ATTRIBUTION = 'marketing.attribution',
  MARKETING_INTENT_SIGNALS = 'marketing.intent_signals',

  // Sales Features
  SALES_LEAD_ROUTING = 'sales.lead_routing',
  SALES_DEMO_SCHEDULING = 'sales.demo_scheduling',
  SALES_QUOTES = 'sales.quotes',
  SALES_FORECASTING = 'sales.forecasting',
  SALES_TERRITORIES = 'sales.territories',

  // RevOps Features
  REVOPS_USAGE_BILLING = 'revops.usage_billing',
  REVOPS_DUNNING = 'revops.dunning',
  REVOPS_REVENUE_RECOGNITION = 'revops.revenue_recognition',

  // Support Features
  SUPPORT_TICKETING = 'support.ticketing',
  SUPPORT_TRAINING = 'support.training',
  SUPPORT_HEALTH_SCORES = 'support.health_scores',
  SUPPORT_RENEWALS = 'support.renewals',

  // Platform Features
  API_ACCESS = 'platform.api_access',
  WEBHOOKS = 'platform.webhooks',
  SSO = 'platform.sso',
  AUDIT_LOGS = 'platform.audit_logs',
  CUSTOM_DOMAIN = 'platform.custom_domain',
  WHITE_LABEL = 'platform.white_label',
  ADVANCED_ANALYTICS = 'platform.advanced_analytics',

  // Integration Features
  INTEGRATION_HEALTHCARE_ACCOUNTS = 'integration.healthcare_accounts',
  INTEGRATION_STRIPE = 'integration.stripe',
  INTEGRATION_CALENDAR = 'integration.calendar',
}

// ============================================================================
// API KEY TYPES
// ============================================================================

export interface TenantApiKey {
  id: string;
  tenantId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  rateLimit: number;
  lastUsedAt?: string;
  expiresAt?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

// ============================================================================
// USER TYPES (Platform-specific extensions)
// ============================================================================

export type PlatformRole = 'tenant_admin' | 'tenant_user' | 'tenant_viewer';

export interface PlatformUser {
  id: string;
  tenantId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: PlatformRole;
  permissions: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// AUDIT LOG TYPES
// ============================================================================

export interface AuditLogEntry {
  id: string;
  tenantId: string;
  userId?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  oldValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  requestId?: string;
  createdAt: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: ApiMeta;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiMeta {
  page?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: ApiMeta & {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// ============================================================================
// PROVISIONING TYPES
// ============================================================================

export interface TenantProvisioningConfig {
  tenantSlug: string;
  tenantName: string;
  planId: string;
  adminEmail: string;
  customDomain?: string;
  branding?: Partial<TenantBranding>;
}

export interface ProvisioningResult {
  success: boolean;
  tenantId?: string;
  tenantSlug?: string;
  adminUserId?: string;
  appUrl?: string;
  error?: string;
}
